import React, { useState, useEffect } from 'react';
import { useBusinessContext } from '../contexts/BusinessContext';
import { Users, Trash2, Edit, Shield, Plus } from 'lucide-react';
import axios from 'axios';
import InviteViewerModal from '../components/InviteViewerModal';
import EditViewerPermissionsModal from '../components/EditViewerPermissionsModal';

const ManageViewers = () => {
  const { currentBusinessId, currentBusiness } = useBusinessContext();
  const [viewers, setViewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedViewer, setSelectedViewer] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (currentBusinessId) {
      fetchViewers();
    }
  }, [currentBusinessId]);

  const fetchViewers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/viewer/list/${currentBusinessId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setViewers(response.data.viewers);
      }
    } catch (err) {
      console.error('Error fetching viewers', err);
      setError('Failed to load viewers');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPermissions = (viewer) => {
    setSelectedViewer(viewer);
    setShowEditModal(true);
  };

  const handleRevokeAccess = async (viewerAccessId, viewerName) => {
    if (!window.confirm(`Are you sure you want to revoke access for ${viewerName}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/viewer/revoke/${viewerAccessId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      fetchViewers();
    } catch (err) {
      console.error('Error revoking access', err);
      alert('Failed to revoke access');
    }
  };

  const permissionLabels = {
    can_view_dashboard: 'Dashboard',
    can_view_business_profile: 'Business Profile',
    can_view_valuation: 'Valuation',
    can_view_gap_assessments: 'Business Gap',
    can_view_personal_gap: 'Personal Gap',
    can_view_exit_strategy: 'Exit Strategy',
    can_view_tasks: 'Tasks',
    can_view_messages: 'Messages',
    can_view_envisioning: 'Envisioning',
    can_view_alerts: 'Alerts',
  };

  if (!currentBusinessId) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">Please select a business to manage viewers.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-3">
              <Users size={28} />
              Manage Viewers
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mt-2">
              {currentBusiness?.business_name || currentBusiness?.name || 'Current Business'}
            </p>
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 min-h-[44px] w-full sm:w-auto"
          >
            <Plus size={20} />
            Invite Viewer
          </button>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
          <h3 className="text-sm sm:text-base font-semibold text-blue-900 mb-2">About Viewers</h3>
          <p className="text-xs sm:text-sm text-blue-800">
            Viewers have read-only access to specific sections of your business.
            They cannot modify any data or invite additional users.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
        </div>
      ) : viewers.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Users size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Viewers Yet</h3>
          <p className="text-gray-600 mb-6">
            Invite viewers to give them read-only access to specific sections.
          </p>
          <button
            onClick={() => setShowInviteModal(true)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 min-h-[44px]"
          >
            Invite Your First Viewer
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {viewers.map((viewer) => {
            const grantedPerms = Object.entries(viewer.permissions)
              .filter(([_, value]) => value)
              .map(([key, _]) => permissionLabels[key]);

            return (
              <div
                key={viewer.id}
                className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition"
              >
                <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                      {viewer.viewer_name}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600 break-words">{viewer.viewer_email}</p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">
                      Added {new Date(viewer.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2 self-end sm:self-start">
                    <button
                      onClick={() => handleEditPermissions(viewer)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
                      title="Edit Permissions"
                    >
                      <Edit size={20} />
                    </button>
                    <button
                      onClick={() => handleRevokeAccess(viewer.id, viewer.viewer_name)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
                      title="Revoke Access"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>

                <div>
                  <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Shield size={16} />
                    Permissions ({grantedPerms.length} granted)
                  </h4>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {grantedPerms.map((label) => (
                      <span
                        key={label}
                        className="px-2 sm:px-3 py-1 bg-green-100 text-green-800 text-xs sm:text-sm rounded-full"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <InviteViewerModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        businessId={currentBusinessId}
        onSuccess={fetchViewers}
      />

      <EditViewerPermissionsModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedViewer(null);
        }}
        viewer={selectedViewer}
        onSuccess={fetchViewers}
      />
    </div>
  );
};

export default ManageViewers;
